/**
 * Venue + facility vocabulary (web).
 *
 * The audit (EVENTS_AUDIT.md §2.2, R2) found **two parallel amenity systems**:
 *
 *   A. `bar_listings.amenities text[]`  — legacy, holds 100% of the live data
 *   B. `venue_facilities` → `facility_definitions` — the good model
 *
 * Phase 3 backfilled B from A (MIGRATION_REPORT.md §3, 16 rows across 3
 * venues) using a hand-authored alias map, because only 1 of 11 live tokens
 * matched a definition slug exactly. `LEGACY_AMENITY_TO_FACILITY` below is
 * that same map, transcribed verbatim.
 *
 * Why the web still needs it: `bar_listings.amenities` is *also* still
 * written by the admin and business venue forms, and `GET /bars` returns it
 * on the row for free. Reading both and taking the union means a venue edited
 * through the legacy form still appears under "pubs with a pool table"
 * instead of quietly vanishing from Places.
 *
 * If the two ever disagree, the structured `venue_facilities` row wins — it
 * carries quantity / price / booking / availability that the flat array
 * cannot express.
 */
import {
  Accessibility, Armchair, Baby, Beer, Building2, Car, Circle, CircleDot,
  Disc3, Dog, Flag, Gamepad2, HelpCircle, Lock, Mic, Mic2, Monitor, Moon,
  Music, Music2, Satellite, Target, Trees, Tv, Utensils, Wifi, Wind, Zap,
  type LucideIcon,
} from "lucide-react";
import type { Tone } from "./tokens";

/* ── Facility icons ───────────────────────────────────────────────────────
   `facility_definitions.icon` stores a kebab-case lucide icon NAME (verified
   against the live table: "circle-dot", "target", "trees", "mic-2"…), not an
   emoji and not a URL. VenueDetail previously rendered it as a character,
   which produced a literal "circle-dot" string where an icon should be — this
   map is what turns that column into the component it always meant.         */

const ICON_BY_NAME: Record<string, LucideIcon> = {
  accessibility: Accessibility,
  armchair: Armchair,
  baby: Baby,
  "building-2": Building2,
  car: Car,
  circle: Circle,
  "circle-dot": CircleDot,
  "disc-3": Disc3,
  dog: Dog,
  flag: Flag,
  "gamepad-2": Gamepad2,
  "help-circle": HelpCircle,
  lock: Lock,
  mic: Mic,
  "mic-2": Mic2,
  monitor: Monitor,
  moon: Moon,
  music: Music,
  "music-2": Music2,
  satellite: Satellite,
  target: Target,
  trees: Trees,
  tv: Tv,
  utensils: Utensils,
  wifi: Wifi,
  wind: Wind,
  zap: Zap,
};

/** Falls back to a neutral glyph rather than rendering the raw icon name. */
export function facilityIcon(iconName: string | null | undefined): LucideIcon {
  return (iconName && ICON_BY_NAME[iconName]) || Circle;
}

/* ── Legacy amenity → facility slug ───────────────────────────────────────
   Transcribed from MIGRATION_REPORT.md §3. Do not "improve" these by
   algorithm: 5 of the 11 have no name-equivalent and were resolved by human
   judgement (darts → dart-board, stage → live-music, karaoke_rig → karaoke,
   outdoor_area → outdoor-seating, accessible → disabled-access).           */

export const LEGACY_AMENITY_TO_FACILITY: Record<string, string> = {
  parking: "parking",
  beer_garden: "beer-garden",
  dog_friendly: "dog-friendly",
  pool_table: "pool-table",
  food_served: "food-served",
  private_hire: "private-hire",
  darts: "dart-board",
  karaoke_rig: "karaoke",
  stage: "live-music",
  accessible: "disabled-access",
  outdoor_area: "outdoor-seating",
  // Present in web/AdminVenues.tsx AMENITIES_LIST but on zero venues today
  // (MIGRATION_REPORT §3 "Frontend tokens with no live row"). Mapped so they
  // work the moment an admin ticks them.
  sports_tv: "sports-tv",
  live_sport: "sports-tv",
  jukebox: "jukebox",
  food: "food-served",
  ufc_boxing: "sports-tv",
};

/** Normalise a legacy `amenities[]` array to facility slugs, dropping unknowns. */
export function amenitiesToFacilitySlugs(amenities: readonly string[] | null | undefined): string[] {
  if (!amenities?.length) return [];
  const out = new Set<string>();
  for (const raw of amenities) {
    const mapped = LEGACY_AMENITY_TO_FACILITY[raw] ?? LEGACY_AMENITY_TO_FACILITY[raw.replace(/-/g, "_")];
    if (mapped) out.add(mapped);
  }
  return [...out];
}

/* ── Venue types ──────────────────────────────────────────────────────── */

const VENUE_TYPE_LABELS: Record<string, string> = {
  pub: "Pub",
  bar: "Bar",
  restaurant: "Restaurant",
  nightclub: "Nightclub",
  sports_bar: "Sports Bar",
  cocktail_bar: "Cocktail Bar",
  social_club: "Social Club",
  hotel: "Hotel",
  cafe: "Café",
};

const VENUE_TYPE_TONES: Record<string, Tone> = {
  pub: "gold",
  bar: "info",
  restaurant: "orange",
  nightclub: "violet",
  sports_bar: "success",
  cocktail_bar: "danger",
  social_club: "primary",
  hotel: "neutral",
  cafe: "orange",
};

export function venueTypeLabel(raw: string | null | undefined): string {
  if (!raw) return "Venue";
  return VENUE_TYPE_LABELS[raw] ?? raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function venueTypeTone(raw: string | null | undefined): Tone {
  return (raw && VENUE_TYPE_TONES[raw]) || "neutral";
}

/**
 * The canonical venue URL.
 *
 * `/venue/<slug>` is the new short form. `/venues/by-slug/<slug>` and
 * `/venues/<id>` still resolve (App.tsx keeps both) because the audit's R10
 * warns that universal links mean any URL we stop honouring breaks installed
 * mobile binaries in the field.
 */
export function venueHref(venue: { id: number; slug?: string | null }): string {
  return venue.slug ? `/venue/${venue.slug}` : `/venues/${venue.id}`;
}

/* ── Places filter row ────────────────────────────────────────────────────
   Two kinds of filter share one chip row because the user does not think of
   them as different questions — "pub" and "has a pool table" are both just
   "what am I looking for".

     kind: "type"     → `bar_listings.venue_type` (a property OF the venue)
     kind: "facility" → a `facility_definitions.slug` (something the venue HAS)

   Labels are overridden from `/api/facility-definitions` at runtime where a
   definition exists, so an admin renaming "Pool Table" is reflected here
   without a deploy.                                                        */

export interface PlaceFilter {
  /** URL segment — `/places/kilmarnock/pool`. */
  slug: string;
  label: string;
  kind: "type" | "facility";
  /** venue_type values (kind=type) or facility_definitions.slug (kind=facility). */
  match: string[];
  icon: LucideIcon;
}

export const PLACE_FILTERS: readonly PlaceFilter[] = [
  { slug: "pubs", label: "Pubs", kind: "type", match: ["pub", "bar", "sports_bar", "cocktail_bar"], icon: Beer },
  { slug: "clubs", label: "Clubs", kind: "type", match: ["nightclub", "social_club"], icon: Disc3 },
  { slug: "pool", label: "Pool", kind: "facility", match: ["pool-table", "snooker-table"], icon: CircleDot },
  { slug: "darts", label: "Darts", kind: "facility", match: ["dart-board"], icon: Target },
  { slug: "live-sport", label: "Live Sport", kind: "facility", match: ["sports-tv", "sky-sports", "tnt-sports", "multiple-screens"], icon: Tv },
  { slug: "jukebox", label: "Jukebox", kind: "facility", match: ["jukebox"], icon: Music },
  { slug: "live-music", label: "Live Music", kind: "facility", match: ["live-music"], icon: Mic },
  { slug: "karaoke", label: "Karaoke", kind: "facility", match: ["karaoke"], icon: Mic2 },
  { slug: "food", label: "Food", kind: "facility", match: ["food-served"], icon: Utensils },
  { slug: "beer-garden", label: "Beer Garden", kind: "facility", match: ["beer-garden", "outdoor-seating"], icon: Trees },
  { slug: "dog-friendly", label: "Dog Friendly", kind: "facility", match: ["dog-friendly"], icon: Dog },
  { slug: "function-room", label: "Function Room", kind: "facility", match: ["function-room", "private-hire"], icon: Building2 },
] as const;

export const PLACE_FILTER_BY_SLUG: Record<string, PlaceFilter> = Object.fromEntries(
  PLACE_FILTERS.map((f) => [f.slug, f]),
);

/** True when a venue satisfies the filter, given its resolved facility slugs. */
export function matchesPlaceFilter(
  filter: PlaceFilter,
  venue: { venueType?: string | null },
  facilitySlugs: ReadonlySet<string>,
): boolean {
  if (filter.kind === "type") {
    return !!venue.venueType && filter.match.includes(venue.venueType);
  }
  return filter.match.some((slug) => facilitySlugs.has(slug));
}

/* ── Event category → facility, for empty-state fallbacks ─────────────────
   THIS IS THE WHOLE POINT OF SPLITTING EVENTS FROM VENUES.

   "No live music tonight" is a dead end. "No live music tonight, but these
   3 venues have a live music stage" is an answer. That second sentence is
   only expressible because the facility lives on the VENUE and the date
   lives on the EVENT — which is exactly the separation this rewrite makes.

   Keys are matched case-insensitively against the messy real
   `events.category` values (the audit found 5 incompatible vocabularies and
   3 values that exist in no table at all).                                 */

export const CATEGORY_TO_FACILITY: Record<string, string[]> = {
  "live music": ["live-music"],
  music: ["live-music"],
  "tribute acts": ["live-music"],
  karaoke: ["karaoke"],
  "quiz night": ["quiz-machine"],
  quiz: ["quiz-machine"],
  "darts night": ["dart-board"],
  darts: ["dart-board"],
  "pool night": ["pool-table"],
  pool: ["pool-table"],
  sport: ["sports-tv"],
  sports: ["sports-tv"],
  "ufc / boxing": ["sports-tv"],
  nightlife: ["late-licence", "dance-floor"],
  comedy: ["live-music"],
  "food & drink": ["food-served"],
};

export function facilitiesForCategory(category: string | null | undefined): string[] {
  if (!category) return [];
  return CATEGORY_TO_FACILITY[category.trim().toLowerCase()] ?? [];
}

/* ── Town slugs ───────────────────────────────────────────────────────────
   Towns are free text everywhere (there is no towns table — audit §1.1), but
   they are clean: every live value matches DEFAULT_TOWNS exactly. Slugging is
   therefore lossless and reversible.                                        */

export function townSlug(town: string): string {
  return town.trim().toLowerCase().replace(/\s+/g, "-");
}

export function townFromSlug(slug: string, towns: readonly string[]): string | null {
  const s = slug.trim().toLowerCase();
  return towns.find((t) => townSlug(t) === s) ?? null;
}
